import React, { useState, useEffect, ChangeEvent } from "react";
import Modal from "../../common/modals";
import Button from "../../common/button";
import { useForm, Controller } from "react-hook-form";
import {
  useLazyGetUserByIdDataQuery,
  useUserUpdateMutation,
} from "../../../store/api/userApi";
import { useParams } from "react-router-dom";
import { Camera, Cross } from "../../../icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { EDIT_PROFILE_SCHEMA } from "../../../helpers/validation-schema";
import Input from "../../common/input/input";
import { useGetCompanySearchDataQuery } from "../../../store/api/searchApi";

interface EditProfileProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormValues {
  banner?: string;
  first_name: string;
  last_name: string;
  username?: string;
  gender?: string;
  dob: string;
  description?: string;
  linkedin?: string;
  instagram?: string;
  x?: string;
  email: string;
  company_name?: string;
  designation?: string;
}

const EditProfile: React.FC<EditProfileProps> = ({ isOpen, onClose }) => {
  const { user_id: userId } = useParams();
  const [refetch] = useLazyGetUserByIdDataQuery();
  const [userData, setUserData] = useState<any>(
    JSON.parse(localStorage.getItem("userData") || "{}"),
  );
  const [userProfile, setUserProfile] = useState<string>(
    userData?.profile_picture || "",
  );
  const [userBanner, setUserBanner] = useState<string>(userData?.banner || "");
  const [triggerUserUpdate] = useUserUpdateMutation();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(EDIT_PROFILE_SCHEMA),
    defaultValues: {
      banner: userData?.banner || "",
      first_name: userData?.first_name || "",
      last_name: userData?.last_name || "",
      gender: userData?.gender || "",
      dob: userData?.dob
        ? new Date(userData?.dob).toISOString().split("T")[0]
        : "",
      description: userData?.description || "",
      linkedin: userData?.socials?.linkedin || "",
      instagram: userData?.socials?.instagram || "",
      x: userData?.socials?.x || "",
      email: userData?.email || "",
      company_name: userData?.company_name || "",
      designation: userData?.designation || "",
    },
  });

  useEffect(() => {
    const currentUserData = userData;
    if (currentUserData) {
      reset({
        banner: currentUserData?.banner || "",
        first_name: currentUserData?.first_name || "",
        last_name: currentUserData?.last_name || "",
        username: currentUserData?.username || "",
        gender: currentUserData?.gender || "",
        dob: currentUserData?.dob
          ? new Date(currentUserData?.dob).toISOString().split("T")[0]
          : "",
        description: currentUserData?.description || "",
        linkedin: currentUserData?.socials?.linkedin || "",
        instagram: currentUserData?.socials?.instagram || "",
        x: currentUserData?.socials?.x || "",
        email: currentUserData?.email || "",
        company_name: currentUserData?.company_name || "",
        designation: currentUserData?.designation || "",
      });
    }
  }, [reset]);

  const onSubmit = async (data: any) => {
    const payload = {} as any;
    if (data.first_name !== userData.first_name) {
      payload.first_name = data.first_name;
    }
    if (data.last_name !== userData.last_name) {
      payload.last_name = data.last_name;
    }
    if (data.email !== userData.email) {
      payload.email = data.email;
    }
    if (data.username !== userData.username) {
      payload.username = data.username;
    }
    if (data.gender !== userData.gender) {
      payload.gender = data.gender;
    }
    if (
      userData.dob &&
      data.dob !== new Date(userData.dob).toISOString().split("T")[0]
    ) {
      payload.dob = data.dob;
    }
    if (data.description !== userData.description) {
      payload.description = data.description;
    }
    if (data.company_name !== userData.company_name) {
      payload.company_name = data.company_name;
    }
    if (data.designation !== userData.designation) {
      payload.designation = data.designation;
    }
    payload.socials = {};
    if (data?.instagram !== userData?.socials?.instagram) {
      payload.socials.instagram = data.instagram;
    }
    if (data?.linkedin !== userData?.socials?.linkedin) {
      payload.socials.linkedin = data.linkedin;
    }
    if (data?.x !== userData?.socials?.x) {
      payload.socials.x = data.x;
    }
    if (userBanner !== userData?.banner && userBanner !== "") {
      payload.banner = userBanner;
    }
    if (userProfile !== userData?.profile_picture && userProfile !== "") {
      payload.profile_picture = userProfile;
    }
    if (payload.socials && Object.keys(payload.socials).length === 0) {
      delete payload.socials;
    }

    console.log("Payload to update:", payload);

    try {
      await triggerUserUpdate(payload);
      console.log("User update successful");
      onClose();
      const updatedUser = await refetch({ userId });
      setUserData(updatedUser?.data?.data);
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const handleFileChange = (
    event: ChangeEvent<HTMLInputElement>,
    type: "profile" | "banner",
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (type === "banner") setUserBanner(reader.result as string);
          else setUserProfile(reader.result as string);
          event.target.value = "";
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const handleCompanyInputChange = (value: string) => {
    setCompanySearchTerm(value || "a");
  };

  const { data: companySearchResults, isLoading } =
    useGetCompanySearchDataQuery(
      { q: companySearchTerm || "a" },
      { skip: false },
    );

  const companyDropdownValues =
    companySearchResults?.Company?.map((company: { name: string }) => ({
      value: company.name,
      label: company.name,
    })) || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customClass="w-full !p-6 h-4/5 overflow-y-hidden"
    >
      <div className=" relative bg-white rounded-lg p-6 m-0 md:mx-2 md:p-0 ">
        <div className="flex items-center justify-between pb-3">
          <h2 className="text-2xl font-bold">Edit Profile</h2>
          <button onClick={onClose}>
            <Cross />
          </button>
        </div>

        <div className="md:w-[110%] w-[100vw] relative -left-8 h-1 bg-gray-100"></div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="h-[600px] overflow-y-auto ">
            <h3 className="text-xl font-semibold py-2">Personal Information</h3>
            <div className="relative w-full h-40 ">
              {userBanner ? (
                <img
                  src={userBanner}
                  alt="banner"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-40 bg-[#007AFF] rounded-lg"></div>
              )}
              <input
                type="file"
                accept=".jpg, .jpeg, .png"
                id="upload-edit-banner"
                className="hidden"
                onChange={(e) => handleFileChange(e, "banner")}
              />
              <label
                htmlFor="upload-edit-banner"
                className="absolute bottom-2 right-2 bg-[#313131] p-1 rounded-xl cursor-pointer"
              >
                <Camera width={16} height={16} />
              </label>
            </div>

            <div className="flex mt-4 gap-2 relative">
              {userProfile ? (
                <div className="w-24 h-20 bg-gray-200 rounded-full overflow-hidden">
                  <img
                    src={userProfile}
                    alt="profile_picture"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-30 h-20 bg-gray-200 rounded-full"></div>
              )}
              <input
                type="file"
                accept=".jpg, .jpeg, .png"
                id="upload-edit-profile"
                className="hidden"
                onChange={(e) => handleFileChange(e, "profile")}
              />
              <label
                htmlFor="upload-edit-profile"
                className="absolute left-14 top-14 bg-[#313131] p-1 rounded-xl cursor-pointer"
              >
                <Camera width={16} height={16} />
              </label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <div className="w-full rounded-lg bg-[#F5F5F5] px-4">
                    <label className="text-sm text-[#999999]">Bio</label>
                    <textarea
                      {...field}
                      className="w-full focus:outline-none resize-none bg-transparent"
                      // placeholder="Bio"
                    />
                    {errors.description && (
                      <span className="text-red-500">Bio is required</span>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="mt-4">
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <div className="w-full rounded-lg bg-[#F5F5F5] px-4 py-2">
                    <label className="text-sm text-[#999999]">First Name</label>
                    <input
                      {...field}
                      className="w-full focus:outline-none bg-transparent"
                      // placeholder="Bio"
                    />
                    {errors.first_name && (
                      <span className="text-red-500">
                        Numbers, special characters or empty first name is not
                        allowed
                      </span>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="mt-4">
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => (
                  <div className="w-full rounded-lg bg-[#F5F5F5] px-4 py-2">
                    <label className="text-sm text-[#999999]">Last Name</label>
                    <input
                      {...field}
                      className="w-full focus:outline-none bg-transparent"
                      // placeholder="Bio"
                    />
                    {errors.last_name && (
                      <span className="text-red-500">
                        Numbers, special characters or empty last name is not
                        allowed
                      </span>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="mt-4">
              <Controller
                name="username"
                control={control}
                disabled={true}
                render={({ field }) => (
                  <div className="w-full rounded-lg bg-[#EBEBE4] px-4 py-2">
                    <label className="text-sm text-[#999999]">Username</label>
                    <input
                      {...field}
                      className="w-full focus:outline-none resize-none bg-transparent"
                      // placeholder="Bio"
                    />
                    {errors.username && (
                      <span className="text-red-500">Username is required</span>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="mt-4">
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <div className="w-full rounded-lg bg-[#F5F5F5] px-4 py-2">
                    <label className="text-sm text-[#999999]">Email</label>
                    <input
                      {...field}
                      className="w-full focus:outline-none resize-none bg-transparent"
                    />
                    {errors.email && (
                      <span className="text-red-500">
                        {errors.email.message}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="mt-4">
              <Controller
                name="company_name"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative">
                    <Input
                      id="company_name"
                      name="company_name"
                      customClassName="bg-[#F5F5F5] text-black px-3"
                      maxLength={30}
                      showError={true}
                      errorMessage={errors?.company_name?.message?.toString()}
                      value={value}
                      onChange={(value) => {
                        onChange(value);
                        handleCompanyInputChange(value);
                      }}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                      placeholder="Company Name"
                    />
                    {isFocused && (
                      <div
                        className="absolute w-full mt-1 z-10 max-h-[160px] overflow-y-auto border border-gray-300 rounded-lg"
                        style={{
                          backgroundColor: "#FFFFFF",
                        }}
                      >
                        {isLoading ? (
                          <div className="p-2 text-gray-300">Loading...</div>
                        ) : (
                          <>
                            {companyDropdownValues.map((option: any) => (
                              <div
                                key={option.value}
                                className="px-6 py-2 cursor-pointer text-black  border-b border-#EBEBEB last:border-b-0"
                                onClick={() => {
                                  onChange(option.value);
                                  setCompanySearchTerm(option.value);
                                  setIsFocused(false);
                                }}
                              >
                                {option.label}
                              </div>
                            ))}
                            {companySearchTerm &&
                              companyDropdownValues.length === 0 && (
                                <div
                                  className="p-2 cursor-pointer text-black hover:bg-gray-100 border-b border-[#F3F4F6] last:border-b-0"
                                  onClick={() => {
                                    onChange(companySearchTerm);
                                    setCompanySearchTerm(companySearchTerm);
                                    setIsFocused(false);
                                  }}
                                >
                                  Add "{companySearchTerm}"
                                </div>
                              )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="mt-4">
              <Controller
                name="designation"
                control={control}
                render={({ field }) => (
                  <div className="w-full rounded-lg bg-[#F5F5F5] px-4 py-2">
                    <label className="text-sm text-[#999999]">
                      Designation
                    </label>
                    <input
                      {...field}
                      className="w-full focus:outline-none resize-none bg-transparent"
                    />
                    {errors.designation && (
                      <span className="text-red-500">
                        {errors.designation.message}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="flex mt-4 space-x-4">
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <div className="w-1/2 rounded-lg bg-[#F5F5F5] px-4 py-2 max-h-[68px]">
                    <label className="text-sm text-[#999999]">Gender</label>
                    <select
                      {...field}
                      className="border-none w-full bg-transparent focus:outline-none"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.gender && (
                      <span className="text-red-500">Gender is required</span>
                    )}
                  </div>
                )}
              />
              <div className="flex flex-col gap-2 w-1/2">
                <Controller
                  name="dob"
                  control={control}
                  render={({ field }) => (
                    <div className="w-full rounded-lg bg-[#F5F5F5] px-4 py-2">
                      <label className="text-sm text-[#999999]">
                        Date of Birth
                      </label>
                      <input
                        {...field}
                        type="date"
                        className="border-none rounded-lg w-full bg-transparent focus:outline-none"
                      />
                    </div>
                  )}
                />
                {errors.dob && (
                  <span className="text-red-500">{errors.dob.message}</span>
                )}
              </div>
            </div>

            <div className="md:w-[110%] w-[100vw] relative -left-8 h-1 bg-gray-100 mt-4"></div>

            <h3 className="text-xl font-semibold py-2">Social Media</h3>
            <div className="pb-2 flex flex-wrap gap-2 pb-72">
              <Controller
                name="linkedin"
                control={control}
                render={({ field }) => (
                  <div className="md:flex-1 w-full rounded-lg bg-[#F5F5F5] px-4 py-2">
                    <label className="text-sm text-[#999999]">LinkedIn</label>
                    <input
                      {...field}
                      className="border-none rounded-lg w-full bg-transparent focus:outline-none"
                    />
                    {errors.linkedin && (
                      <span className="text-red-500">
                        {errors.linkedin.message}
                      </span>
                    )}
                  </div>
                )}
              />

              <Controller
                name="instagram"
                control={control}
                render={({ field }) => (
                  <div className="md:flex-1 w-full rounded-lg bg-[#F5F5F5] px-4 py-2">
                    <label className="text-sm text-[#999999]">Instagram</label>
                    <input
                      {...field}
                      className="border-none rounded-lg w-full bg-transparent focus:outline-none"
                    />
                    {errors.instagram && (
                      <span className="text-red-500">
                        {errors.instagram.message}
                      </span>
                    )}
                  </div>
                )}
              />

              <Controller
                name="x"
                control={control}
                render={({ field }) => (
                  <div className="w-full rounded-lg bg-[#F5F5F5] px-4 py-2">
                    <label className="text-sm text-[#999999]">
                      X (formerly Twitter)
                    </label>
                    <input
                      {...field}
                      className="border-none rounded-lg w-full bg-transparent focus:outline-none"
                    />
                    {errors.x && (
                      <span className="text-red-500">{errors.x.message}</span>
                    )}
                  </div>
                )}
              />
            </div>
          </div>
          <div className="sticky -bottom-6 absolute z-[10] bg-white h-full py-6">
            <Button isSubmitting={isSubmitting} type="submit">
              Save
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditProfile;
